import { Cross2Icon } from "@radix-ui/react-icons";
import * as Popover from "@radix-ui/react-popover";
import {
  Auth0AccountRole,
  OrganizationResponseForAdmins,
} from "client/openapi";
// The calendar's quick Book Meeting pop-up's look: gutter-booking-popover,
// gutter-booking (its width) and gutter-booking__close; and its dragging.
import "components/Dashboard/Calendar/GutterBooking/index.css";
import DraggableContent from "components/Dashboard/Calendar/GutterBooking/DraggableContent";
import { NewTaskForm } from "components/Dashboard/ManageTasksDialog/create";
import { TaskPerson } from "components/Dashboard/ManageTasksDialog/PeoplePicker";
import { announceTasksChanged } from "components/Dashboard/ManageTasksDialog/tasksChanged";
import IconTip from "components/Dashboard/PeoplePanel/IconTip";
import { useContext, useRef, useState } from "react";
import { LuListPlus } from "react-icons/lu";
import { OrgRolesAndAccountContext } from "util/OrgRolesAccountContext";
import { CARD_KINDS } from "./CardChips";

// The pop-out's field ids: its Task name is `${FIELDS}-name`.
const FIELDS = "board-task";

/**
 * A board card's Create task. The icon left of the clipboard opens the New
 * task form in a pop-out beside the card, as the calendar's quick Book
 * Meeting pop-up opens beside its time. The task is about the card's person
 * and assigned to the signed-in admin, with the cursor in Task name, so a
 * name and Create task make it. A save reloads the cards' counts
 * (onCreated), so the card's Tasks chip shows it, and tells the page's other
 * task lists. Its header drags it anywhere in the window (DraggableContent).
 * `button` renders a card's icon, which opens the pop-out and,
 * clicked again, closes it; `popout` the open pop-out (outside the cards, so
 * nothing in it reaches a card). The pickers list the CRM page's
 * `organization`'s people.
 */
export default function useCardTask(
  organization: OrganizationResponseForAdmins,
  onCreated: () => void
) {
  const { account } = useContext(OrgRolesAndAccountContext);
  // Whom the open pop-out's task is about.
  const [about, setAbout] = useState<TaskPerson>();
  // The card it opens beside, and the icon that opened it.
  const card = useRef<HTMLElement | null>(null);
  const opener = useRef<HTMLButtonElement | null>(null);
  const form = useRef<HTMLDivElement>(null);

  const close = () => setAbout(undefined);

  const button = (personType: Auth0AccountRole, id: number, label: string) => (
    <IconTip label="Create task">
      <button
        type="button"
        className="board-card-open"
        aria-label={`Create task for ${label}`}
        // A press on it never drags the card.
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        onClick={(e) => {
          // A second click on the open pop-out's icon closes it.
          const again = !!about && opener.current === e.currentTarget;
          opener.current = e.currentTarget;
          card.current = e.currentTarget.closest(".visualization-card");
          setAbout(
            again ? undefined : { role: CARD_KINDS[personType], id, label }
          );
        }}
      >
        <LuListPlus aria-hidden strokeWidth={2.5} />
      </button>
    </IconTip>
  );

  const popout = about && (
    <Popover.Root open onOpenChange={(open) => !open && close()}>
      <Popover.Anchor virtualRef={card} />
      <Popover.Portal>
        <DraggableContent
          // Each card's pop-out opens beside the card.
          resetKey={`${about.role}-${about.id}`}
          className="gutter-booking-popover board-task-popover"
          side="right"
          align="start"
          sideOffset={8}
          collisionPadding={12}
          aria-label="New task"
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            document.getElementById(`${FIELDS}-name`)?.focus();
          }}
          onCloseAutoFocus={(e) => e.preventDefault()}
          // A press on its own icon is left to the icon's click.
          onInteractOutside={(e) => {
            if (opener.current?.contains(e.target as Node)) e.preventDefault();
          }}
        >
          <div ref={form} className="gutter-booking task-form board-task">
            <div className="board-task__header" data-drag-handle>
              <h2 className="task-form__title">New task</h2>
              <button
                type="button"
                className="gutter-booking__close"
                aria-label="Close"
                onClick={close}
              >
                <Cross2Icon width={18} height={18} />
              </button>
            </div>
            <NewTaskForm
              key={`${about.role}-${about.id}`}
              students={organization.students}
              parents={organization.parents}
              tutors={organization.tutors}
              admins={organization.admins}
              initialTask={{
                tagged: [about],
                assigned: account
                  ? [
                      {
                        role: "admin",
                        id: account.reference_id,
                        label: `${account.first_name} ${account.last_name}`,
                      },
                    ]
                  : [],
              }}
              suggested={[about]}
              idPrefix={FIELDS}
              // Inside the pop-out, so the calendar takes clicks.
              dateContainer={() => form.current || document.body}
              // The pop-out is fixed to the window.
              fitMenusToWindow
              onCreated={() => {
                close();
                announceTasksChanged();
                onCreated();
              }}
            />
          </div>
        </DraggableContent>
      </Popover.Portal>
    </Popover.Root>
  );

  return { button, popout };
}
